import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import '../quotes/quote-generator.css';
import { apiRequest } from '../../../api/client';
import ClientDevicesSection from './ClientDevicesSection';

const empty = {
    firstName: '',
    lastName: '',
    buildingName: '',
    address: '',
    phone: '',
    email: '',
    corpNo: '',
    representativeName: '',
    propertyType: '',
    propertySize: '',
    systemInfo: '',
    access: '',
    facilities: '',
    commonArea: ''
};

type ClientPayload = typeof empty;

export default function NewClientPage(): React.JSX.Element {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);

    const [form, setForm] = useState<ClientPayload>(empty);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(false);

    useEffect(() => {
        if (!id) {
            setLoading(false);
            return;
        }
        setLoadError(null);
        setLoading(true);
        apiRequest<{ data: Record<string, unknown> }>(`/api/clients/${id}`)
            .then((res) => {
                const d = res.data;
                setForm({
                    firstName: String(d.firstName ?? ''),
                    lastName: String(d.lastName ?? ''),
                    buildingName: String(d.buildingName ?? ''),
                    address: String(d.address ?? ''),
                    phone: String(d.phone ?? ''),
                    email: String(d.email ?? ''),
                    corpNo: String(d.corpNo ?? ''),
                    representativeName: String(d.representativeName ?? ''),
                    propertyType: String(d.propertyType ?? ''),
                    propertySize: String(d.propertySize ?? ''),
                    systemInfo: String(d.systemInfo ?? ''),
                    access: String(d.access ?? ''),
                    facilities: String(d.facilities ?? ''),
                    commonArea: String(d.commonArea ?? '')
                });
            })
            .catch((err: unknown) => {
                setLoadError(err instanceof Error ? err.message : 'Could not load client.');
            })
            .finally(() => setLoading(false));
    }, [id]);

    const set =
        (field: keyof ClientPayload) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setForm((prev) => ({ ...prev, [field]: e.target.value }));
        };

    const validate = (): boolean => {
        const digits = form.phone.replace(/\D/g, '');
        if (!form.firstName.trim() && !form.lastName.trim()) {
            setErrorMessage('Enter a first name, last name, or both.');
            return false;
        }
        if (!form.address.trim()) {
            setErrorMessage('Property address is required.');
            return false;
        }
        if (!form.email.trim() && digits.length < 10) {
            setErrorMessage('Provide an email or a phone number with at least 10 digits.');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage(null);
        if (!validate()) return;
        setSaving(true);
        try {
            if (isEdit && id) {
                await apiRequest(`/api/clients/${id}`, {
                    method: 'PATCH',
                    json: form
                });
            } else {
                await apiRequest<{ data: { _id: string } }>('/api/clients', {
                    method: 'POST',
                    json: form
                });
            }
            navigate('/admin/clients');
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Could not save client.';
            setErrorMessage(message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClient = async () => {
        if (!id) return;
        const ok = window.confirm(
            'Delete this client? They will be hidden from your lists and search. The record is kept in the database (inactive).'
        );
        if (!ok) return;
        setErrorMessage(null);
        setRemoving(true);
        try {
            await apiRequest(`/api/clients/${id}`, {
                method: 'PATCH',
                json: { action: 'deactivate' }
            });
            navigate('/admin/clients');
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Could not delete client.';
            setErrorMessage(message);
        } finally {
            setRemoving(false);
        }
    };

    return (
        <AppShell>
            <div className="quote-page">
                <div className="quote-container" style={{ maxWidth: isEdit ? '1120px' : '720px' }}>
                    <header className="quote-header" style={{ marginBottom: '22px' }}>
                        <div>
                            <h1 style={{ fontSize: '2.2rem', marginBottom: '8px' }}>
                                {isEdit ? 'Edit client' : 'New client'}
                            </h1>
                            <p style={{ color: 'var(--dash-muted)', margin: 0, fontSize: '0.98rem' }}>
                                Saved clients appear when you search in quotes and invoices.
                            </p>
                        </div>
                        <button type="button" className="btn-icon" onClick={() => navigate('/admin/clients')}>
                            ✕
                        </button>
                    </header>

                    {loadError && <div className="banner banner-error">{loadError}</div>}
                    {errorMessage && <div className="banner banner-error">{errorMessage}</div>}

                    {loading ? (
                        <div className="quote-card" style={{ padding: '28px', color: 'var(--dash-muted)' }}>
                            Loading client…
                        </div>
                    ) : loadError && isEdit ? null : (
                        <form onSubmit={handleSubmit}>
                            <section className="quote-card">
                                <div className="quote-section-title">Contact</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">First name</label>
                                        <input
                                            className="form-input"
                                            value={form.firstName}
                                            onChange={set('firstName')}
                                            autoComplete="given-name"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Last name</label>
                                        <input
                                            className="form-input"
                                            value={form.lastName}
                                            onChange={set('lastName')}
                                            autoComplete="family-name"
                                        />
                                    </div>
                                    <div className="form-group full-width">
                                        <label className="form-label">Building / site name</label>
                                        <input
                                            className="form-input"
                                            value={form.buildingName}
                                            onChange={set('buildingName')}
                                        />
                                    </div>
                                    <div className="form-group full-width">
                                        <label className="form-label">Property address *</label>
                                        <input
                                            className="form-input"
                                            required
                                            value={form.address}
                                            onChange={set('address')}
                                            autoComplete="street-address"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Email</label>
                                        <input
                                            className="form-input"
                                            type="email"
                                            value={form.email}
                                            onChange={set('email')}
                                            autoComplete="email"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Phone</label>
                                        <input
                                            className="form-input"
                                            type="tel"
                                            value={form.phone}
                                            onChange={set('phone')}
                                            autoComplete="tel"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Corp no.</label>
                                        <input className="form-input" value={form.corpNo} onChange={set('corpNo')} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Representative</label>
                                        <input
                                            className="form-input"
                                            value={form.representativeName}
                                            onChange={set('representativeName')}
                                        />
                                    </div>
                                </div>
                                <p
                                    style={{
                                        margin: '18px 0 0',
                                        fontSize: '0.88rem',
                                        color: 'var(--dash-muted)',
                                        lineHeight: 1.5
                                    }}
                                >
                                    * Address is required. Enter at least a first or last name, and either a valid email
                                    or a phone number with at least 10 digits.
                                </p>
                            </section>

                            <section className="quote-card" style={{ marginTop: '18px' }}>
                                <div className="quote-section-title">Property details</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Property type</label>
                                        <input
                                            className="form-input"
                                            value={form.propertyType}
                                            onChange={set('propertyType')}
                                            placeholder="e.g. Commercial, residential high-rise"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Property size</label>
                                        <input
                                            className="form-input"
                                            value={form.propertySize}
                                            onChange={set('propertySize')}
                                            placeholder="e.g. sq ft, floors, units"
                                        />
                                    </div>
                                    <div className="form-group full-width">
                                        <label className="form-label">System info</label>
                                        <textarea
                                            className="form-textarea"
                                            rows={3}
                                            value={form.systemInfo}
                                            onChange={set('systemInfo')}
                                            placeholder="Alarm panels, sprinklers, standpipe, etc."
                                        />
                                    </div>
                                    <div className="form-group full-width">
                                        <label className="form-label">Access</label>
                                        <textarea
                                            className="form-textarea"
                                            rows={2}
                                            value={form.access}
                                            onChange={set('access')}
                                            placeholder="Keys, codes, escort, after-hours access"
                                        />
                                    </div>
                                    <div className="form-group full-width">
                                        <label className="form-label">Facilities</label>
                                        <textarea
                                            className="form-textarea"
                                            rows={2}
                                            value={form.facilities}
                                            onChange={set('facilities')}
                                            placeholder="Relevant building facilities"
                                        />
                                    </div>
                                    <div className="form-group full-width">
                                        <label className="form-label">Common area</label>
                                        <textarea
                                            className="form-textarea"
                                            rows={2}
                                            value={form.commonArea}
                                            onChange={set('commonArea')}
                                            placeholder="Lobbies, corridors, shared spaces"
                                        />
                                    </div>
                                </div>
                            </section>

                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '12px',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginTop: '20px'
                                }}
                            >
                                <div>
                                    {isEdit && id ? (
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            disabled={saving || removing}
                                            onClick={handleDeleteClient}
                                            style={{
                                                color: '#b91c1c',
                                                borderColor: 'rgba(185, 28, 28, 0.45)'
                                            }}
                                        >
                                            {removing ? 'Deleting…' : 'Delete'}
                                        </button>
                                    ) : null}
                                </div>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        disabled={saving || removing}
                                        onClick={() => navigate('/admin/clients')}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primary" disabled={saving || removing}>
                                        {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save client'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                    {isEdit && id ? <ClientDevicesSection clientId={id} /> : null}
                </div>
            </div>
        </AppShell>
    );
}
